import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Menu, Bell, User, LogOut, Settings, ChevronDown, Building2, Mail, Phone, Globe, MapPin, CreditCard, ShieldCheck, X } from 'lucide-react';
import { useQuery } from 'react-query';
import api from '../../utils/api';
import toast from 'react-hot-toast';
import { useAuth } from '../../contexts/AuthContext';
import { useBranch } from '../../contexts/BranchContext';

const Header = ({ onMenuClick }) => {
  const navigate = useNavigate();
  const { user, logout, currencySymbol } = useAuth();
  const { branches, selectedBranch, switchBranch } = useBranch();
  const [showUserMenu, setShowUserMenu] = useState(false);
  const [showBranchMenu, setShowBranchMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showProfileModal, setShowProfileModal] = useState(false);
  const [readNotifications, setReadNotifications] = useState(() => {
    // Load read notifications from localStorage
    const saved = localStorage.getItem('readNotifications');
    return saved ? JSON.parse(saved) : [];
  });

  // Fetch company settings for logo and name
  const { data: companySettings } = useQuery(
    'company-settings',
    async () => {
      const response = await api.get('/api/settings/company');
      return response.data.data;
    }
  );

  // Fetch real notifications
  const { data: notificationsData } = useQuery(
    ['notifications', selectedBranch?.id],
    async () => {
      const response = await api.get('/api/dashboard/overview', {
        params: { branchId: selectedBranch?.id }
      });
      return response.data.data;
    },
    {
      enabled: !!selectedBranch,
      refetchInterval: 60000, // Refetch every minute
    }
  );

  // Generate real notifications from data
  const generateNotifications = () => {
    const notifications = [];

    // Google Drive missing notification
    if (companySettings && !companySettings.googleDriveConnected) {
      notifications.push({
        id: 'google-drive-disconnected',
        type: 'danger',
        color: 'red',
        title: 'Google Drive Disconnected',
        message: 'Please link your Google Drive in settings to enable isolated tenant uploads.',
        time: 'Action Required',
        action: () => navigate('/settings')
      });
    }
    
    if (notificationsData) {
      // Pending jobs notification
      if (notificationsData.secondaryMetrics?.pendingJobs > 0) {
        const notifId = `pending-jobs-${notificationsData.secondaryMetrics.pendingJobs}`;
        if (!readNotifications.includes(notifId)) {
          notifications.push({
            id: notifId,
            type: 'warning',
            color: 'yellow',
            title: 'Pending Jobs',
            message: `You have ${notificationsData.secondaryMetrics.pendingJobs} pending job(s) awaiting action`,
            time: 'Now'
          });
        }
      }

      // Low stock notifications
      if (notificationsData.stockWarnings && notificationsData.stockWarnings.length > 0) {
        const lowStockCount = notificationsData.stockWarnings.length;
        const notifId = `low-stock-${lowStockCount}`;
        if (!readNotifications.includes(notifId)) {
          notifications.push({
            id: notifId,
            type: 'alert',
            color: 'red',
            title: 'Low Stock Alert',
            message: `${lowStockCount} product(s) are running low on stock`,
            time: 'Today'
          });
        }
      }

      // Recent jobs notification
      if (notificationsData.recentJobs && notificationsData.recentJobs.length > 0) {
        const latestJob = notificationsData.recentJobs[0];
        const timeAgo = getTimeAgo(new Date(latestJob.createdAt));
        const notifId = `job-${latestJob.id}`;
        if (!readNotifications.includes(notifId)) {
          notifications.push({
            id: notifId,
            type: 'info',
            color: 'blue',
            title: 'New Job Created',
            message: `Job ${latestJob.jobNumber} has been created for ${latestJob.customer.name}`,
            time: timeAgo
          });
        }
      }

      // Profit notification
      if (notificationsData.primaryMetrics?.profit > 0) {
        const profit = notificationsData.primaryMetrics.profit;
        const notifId = `profit-${Math.floor(profit)}`;
        if (!readNotifications.includes(notifId)) {
          notifications.push({
            id: notifId,
            type: 'success',
            color: 'green',
            title: 'Profit Update',
            message: `Current profit: ${currencySymbol}${profit.toLocaleString()}`,
            time: 'Today'
          });
        }
      }
    }

    return notifications;
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - date) / 1000);
    
    if (seconds < 60) return 'Just now';
    if (seconds < 3600) return `${Math.floor(seconds / 60)} minutes ago`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hours ago`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} days ago`;
    return date.toLocaleDateString();
  };

  const notifications = generateNotifications();

  const handleLogout = () => {
    logout();
    setShowUserMenu(false);
  };

  const handleNotificationClick = () => {
    setShowNotifications(!showNotifications);
  };

  const handleSettingsClick = () => {
    setShowUserMenu(false);
    navigate('/settings');
  };

  const handleMarkAllAsRead = () => {
    const allNotifIds = notifications.map(n => n.id);
    const updatedReadNotifs = [...readNotifications, ...allNotifIds];
    setReadNotifications(updatedReadNotifs);
    localStorage.setItem('readNotifications', JSON.stringify(updatedReadNotifs));
    setShowNotifications(false);
    toast.success('All notifications marked as read');
  };

  return (
    <header className="bg-white shadow-sm border-b border-gray-200">
      <div className="flex items-center justify-between px-4 py-3 sm:px-6 sm:py-4">
        {/* Left side */}
        <div className="flex items-center space-x-4">
          {/* Mobile menu button */}
          <button
            onClick={onMenuClick}
            className="lg:hidden p-2 rounded-md text-gray-600 hover:text-gray-900 hover:bg-gray-100"
          >
            <Menu className="w-6 h-6" />
          </button>

          {/* Logo and title */}
          <div className="flex items-center space-x-3">
            {companySettings?.logo ? (
              <img
                src={companySettings.logo.startsWith('http') ? companySettings.logo : `${api.defaults.baseURL || ''}${companySettings.logo}`}
                alt="Logo"
                className="w-8 h-8 rounded-lg object-contain bg-slate-50 p-0.5 border"
                onError={(e) => {
                  e.target.style.display = 'none';
                }}
              />
            ) : (
              <div className="w-8 h-8 bg-blue-600 rounded-lg flex items-center justify-center">
                <span className="text-white font-bold text-sm">RS</span>
              </div>
            )}
            <h1 className="text-xl font-semibold text-gray-900 hidden sm:block">
              {companySettings?.companyName || 'Repair Shop Management'}
            </h1>
          </div>
        </div>

        {/* Right side */}
        <div className="flex items-center space-x-4">
          {/* Branch Selector */}
          {branches.length > 1 && (
            <div className="relative">
              <button
                onClick={() => setShowBranchMenu(!showBranchMenu)}
                className="flex items-center space-x-2 px-3 py-2 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                <span>{selectedBranch?.name || 'Select Branch'}</span>
                <ChevronDown className="w-4 h-4" />
              </button>

              {showBranchMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                  {branches.map((branch) => (
                    <button
                      key={branch.id}
                      onClick={() => {
                        switchBranch(branch);
                        setShowBranchMenu(false);
                      }}
                      className={`block w-full text-left px-4 py-2 text-sm hover:bg-gray-100 ${
                        selectedBranch?.id === branch.id
                          ? 'bg-blue-50 text-blue-700'
                          : 'text-gray-700'
                      }`}
                    >
                      <div>
                        <div className="font-medium">{branch.name}</div>
                        <div className="text-xs text-gray-500">{branch.code}</div>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Notifications */}
          <div className="relative">
            <button 
              onClick={handleNotificationClick}
              className="p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg relative"
            >
              <Bell className="w-5 h-5" />
              {notifications.length > 0 && (
                <span className="absolute top-1 right-1 w-3 h-3 bg-red-500 rounded-full"></span>
              )}
            </button>

            {showNotifications && (
              <div className="absolute right-0 mt-2 w-80 bg-white rounded-md shadow-lg py-2 z-50 border border-gray-200">
                <div className="px-4 py-2 border-b border-gray-100">
                  <h3 className="text-sm font-semibold text-gray-900">Notifications</h3>
                </div>
                
                <div className="max-h-96 overflow-y-auto">
                  {notifications.length === 0 ? (
                    <div className="px-4 py-8 text-center">
                      <Bell className="w-12 h-12 text-gray-300 mx-auto mb-2" />
                      <p className="text-sm text-gray-500">No new notifications</p>
                    </div>
                  ) : (
                    notifications.map((notification) => (
                      <div 
                        key={notification.id} 
                        onClick={() => {
                          if (notification.action) {
                            notification.action();
                          }
                          setShowNotifications(false);
                        }}
                        className="px-4 py-3 hover:bg-gray-50 cursor-pointer border-b border-gray-100"
                      >
                        <div className="flex items-start space-x-3">
                          <div className={`w-2 h-2 bg-${notification.color}-500 rounded-full mt-2`}></div>
                          <div className="flex-1">
                            <p className="text-sm text-gray-900 font-medium">{notification.title}</p>
                            <p className="text-xs text-gray-550 mt-1">{notification.message}</p>
                            <p className="text-xs text-gray-400 mt-1">{notification.time}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>

                {notifications.length > 0 && (
                  <div className="px-4 py-2 border-t border-gray-100">
                    <button 
                      onClick={handleMarkAllAsRead}
                      className="text-sm text-blue-600 hover:text-blue-800 font-medium w-full text-center"
                    >
                      Mark all as read
                    </button>
                  </div>
                )}
              </div>
            )}
          </div>

          {/* User menu */}
          <div className="relative">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center space-x-3 p-2 text-gray-600 hover:text-gray-900 hover:bg-gray-100 rounded-lg"
            >
              <div className="w-8 h-8 bg-gray-300 rounded-full flex items-center justify-center">
                <User className="w-5 h-5" />
              </div>
              <div className="hidden md:block text-left">
                <div className="text-sm font-medium text-gray-900">
                  {user?.role === 'CLIENT' ? user?.name : `${user?.firstName || ''} ${user?.lastName || ''}`.trim()}
                </div>
                <div className="text-xs text-gray-500">
                  {user?.role === 'CLIENT' ? user?.shopName : user?.role}
                </div>
              </div>
              <ChevronDown className="w-4 h-4" />
            </button>

            {showUserMenu && (
              <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg py-1 z-50 border border-gray-200">
                <div className="px-4 py-2 border-b border-gray-100">
                  <div className="text-sm font-medium text-gray-900">
                    {user?.role === 'CLIENT' ? user?.name : `${user?.firstName || ''} ${user?.lastName || ''}`.trim()}
                  </div>
                  <div className="text-xs text-gray-500">{user?.email || 'kuma60asamy909@gmail.com'}</div>
                </div>

                {user?.role === 'CLIENT' && (
                  <button
                    onClick={() => {
                      setShowUserMenu(false);
                      setShowProfileModal(true);
                    }}
                    className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                  >
                    <Building2 className="w-4 h-4 mr-3 text-blue-600" />
                    Business Profile
                  </button>
                )}
                
                <button
                  onClick={handleSettingsClick}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <Settings className="w-4 h-4 mr-3" />
                  Settings
                </button>
                
                <button
                  onClick={handleLogout}
                  className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-100"
                >
                  <LogOut className="w-4 h-4 mr-3" />
                  Logout
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Business Profile Details Modal */}
      {showProfileModal && user?.role === 'CLIENT' && (
        <div className="fixed inset-0 bg-slate-900/60 backdrop-blur-sm flex items-center justify-center z-[9999] p-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg overflow-hidden border border-slate-100 flex flex-col max-h-[90vh]">
            {/* Modal Header */}
            <div className="bg-gradient-to-r from-blue-600 to-indigo-750 text-white px-6 py-4 flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Building2 className="w-5 h-5" />
                <h2 className="text-lg font-bold">Business Profile Details</h2>
              </div>
              <button
                onClick={() => setShowProfileModal(false)}
                className="text-white/80 hover:text-white bg-white/10 hover:bg-white/20 p-1.5 rounded-lg transition-all"
              >
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Modal Body */}
            <div className="p-6 overflow-y-auto space-y-6">
              {/* Shop Title and Status */}
              <div className="flex justify-between items-start border-b pb-4">
                <div>
                  <h3 className="text-xl font-extrabold text-slate-900">{user?.shopName}</h3>
                  <p className="text-sm text-slate-550 mt-1 flex items-center gap-1.5 font-semibold">
                    <User className="w-3.5 h-3.5 text-blue-600" /> Owner: {user?.name}
                  </p>
                </div>
                <span className="bg-emerald-50 text-emerald-700 text-xs px-2.5 py-1 rounded-full font-bold border border-emerald-200 uppercase">
                  Active
                </span>
              </div>

              {/* Information Grid */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-left">
                <div className="space-y-1">
                  <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Email Address</span>
                  <div className="flex items-center gap-2 text-sm text-slate-700 font-semibold truncate">
                    <Mail className="w-4 h-4 text-blue-600 shrink-0" />
                    <span>{user?.email || 'kuma60asamy909@gmail.com'}</span>
                  </div>
                </div>

                <div className="space-y-1">
                  <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Phone Number</span>
                  <div className="flex items-center gap-2 text-sm text-slate-700 font-semibold">
                    <Phone className="w-4 h-4 text-blue-600 shrink-0" />
                    <span>{user?.phone || 'Not Provided'}</span>
                  </div>
                </div>

                <div className="space-y-1">
                  <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">WhatsApp Contact</span>
                  <div className="flex items-center gap-2 text-sm text-slate-700 font-semibold">
                    <Phone className="w-4 h-4 text-emerald-600 shrink-0" />
                    <span>{user?.whatsapp || user?.phone || 'Not Provided'}</span>
                  </div>
                </div>

                <div className="space-y-1">
                  <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">GST Number</span>
                  <div className="flex items-center gap-2 text-sm text-slate-700 font-semibold">
                    <ShieldCheck className="w-4 h-4 text-indigo-600 shrink-0" />
                    <span className="font-mono">{user?.gst || 'Not Provided'}</span>
                  </div>
                </div>

                <div className="space-y-1 md:col-span-2">
                  <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Website</span>
                  <div className="flex items-center gap-2 text-sm text-slate-700 font-semibold">
                    <Globe className="w-4 h-4 text-blue-600 shrink-0" />
                    {user?.website ? (
                      <a href={user.website.startsWith('http') ? user.website : `https://${user.website}`} target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">
                        {user.website}
                      </a>
                    ) : (
                      <span>Not Provided</span>
                    )}
                  </div>
                </div>

                <div className="space-y-1 md:col-span-2">
                  <span className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Billing Address</span>
                  <div className="flex items-start gap-2 text-sm text-slate-700 font-semibold">
                    <MapPin className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                    <div>
                      <p>{user?.address || 'Not Provided'}</p>
                      {(user?.city || user?.state || user?.pincode) && (
                        <p className="text-xs text-slate-500 mt-0.5 font-normal">
                          {user?.city || ''}, {user?.state || ''} - {user?.pincode || ''}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* Plan Details */}
              <div className="bg-slate-50 rounded-xl p-4 border border-slate-100 flex items-center justify-between text-left">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-lg bg-blue-100 flex items-center justify-center text-blue-700">
                    <CreditCard className="w-5 h-5" />
                  </div>
                  <div>
                    <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Subscription Tier</p>
                    <p className="text-sm font-extrabold text-slate-800">{user?.plan || 'Starter'} Plan ({user?.planData?.cycle || 'Monthly'})</p>
                  </div>
                </div>
                <div className="text-right">
                  <p className="text-[10px] text-slate-400 font-bold uppercase tracking-wider">Next Renewal</p>
                  <p className="text-xs font-bold text-slate-800 font-mono">{user?.nextBilling || 'Not Set'}</p>
                </div>
              </div>
            </div>

            {/* Modal Footer */}
            <div className="bg-slate-50 px-6 py-4 flex justify-end border-t border-slate-100">
              <button
                onClick={() => setShowProfileModal(false)}
                className="bg-blue-600 hover:bg-blue-500 text-white font-bold px-6 py-2 rounded-xl text-sm transition-all shadow-sm active:scale-95"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </header>
  );
};

export default Header;
